'use client';

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import type { LoginDto } from '@nomiss/sdk';
import {
  ApiRequestError,
  bootstrapAdminSdkAuth,
  clearAdminSession,
  getMyProfileV2,
  hasAdminRole,
  isSuperAdminRole,
  loadAdminSession,
  loginWithPassword,
  readAccessTokenPermissions,
  saveAdminSession,
  subscribeAdminSession,
  type UserProfileV2,
} from '@nomiss/services';

export class AdminAuthError extends Error {
  constructor(
    message: string,
    readonly code: 'NOT_ADMIN' | 'INACTIVE' | 'UNAUTHORIZED',
  ) {
    super(message);
    this.name = 'AdminAuthError';
  }
}

export type AdminAuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

type AdminAuthContextValue = {
  status: AdminAuthStatus;
  isAuthenticated: boolean;
  email: string | null;
  roles: string[];
  profile: UserProfileV2 | null;
  establishSession: (profile: UserProfileV2) => void;
  logout: () => void;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

async function fetchAdminProfile(accessToken: string): Promise<UserProfileV2> {
  const profile = await getMyProfileV2(accessToken);

  if (!profile.isActive) {
    throw new AdminAuthError('This account has been blocked', 'INACTIVE');
  }

  if (!hasAdminRole(profile.roles)) {
    throw new AdminAuthError(
      'This account does not have admin access.',
      'NOT_ADMIN',
    );
  }

  return profile;
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AdminAuthStatus>('loading');
  const [profile, setProfile] = useState<UserProfileV2 | null>(null);

  useEffect(() => {
    let cancelled = false;

    bootstrapAdminSdkAuth();

    const session = loadAdminSession();
    if (!session) {
      setStatus('unauthenticated');
      return;
    }

    void fetchAdminProfile(session.accessToken)
      .then((nextProfile) => {
        if (cancelled) return;
        setProfile(nextProfile);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        clearAdminSession();
        setProfile(null);
        setStatus('unauthenticated');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const establishSession = useCallback((nextProfile: UserProfileV2) => {
    setProfile(nextProfile);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(() => {
    clearAdminSession();
    setProfile(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      status,
      isAuthenticated: status === 'authenticated',
      email: profile?.email ?? null,
      roles: profile?.roles ?? [],
      profile,
      establishSession,
      logout,
    }),
    [status, profile, establishSession, logout],
  );

  return createElement(AdminAuthContext.Provider, { value }, children);
}

export function useAdminSession(): AdminAuthContextValue {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminSession must be used within AdminAuthProvider');
  }
  return context;
}

export type AdminLoginResult = {
  profile: UserProfileV2;
};

export function useAdminLogin() {
  const adminLoginMutation = useMutation({
    mutationFn: async (dto: LoginDto): Promise<AdminLoginResult> => {
      let tokens;
      try {
        tokens = await loginWithPassword({
          email: dto.email.trim().toLowerCase(),
          password: dto.password,
        });
      } catch (error) {
        if (error instanceof ApiRequestError) {
          throw error;
        }
        throw error;
      }

      bootstrapAdminSdkAuth();
      saveAdminSession({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });

      try {
        const profile = await fetchAdminProfile(tokens.accessToken);
        return { profile };
      } catch (error) {
        clearAdminSession();
        throw error;
      }
    },
  });

  return { adminLoginMutation };
}

export function useAdminRoleGate() {
  const { roles, isAuthenticated } = useAdminSession();
  const isSuperAdmin = isAuthenticated && isSuperAdminRole(roles);
  const [sessionRevision, setSessionRevision] = useState(0);

  useEffect(() => {
    return subscribeAdminSession(() => {
      setSessionRevision((revision) => revision + 1);
    });
  }, []);

  const permissions = useMemo(() => {
    if (!isAuthenticated) return [];
    const session = loadAdminSession();
    if (!session) return [];
    return readAccessTokenPermissions(session.accessToken);
  }, [isAuthenticated, sessionRevision]);

  const hasPermission = useCallback(
    (permission: string) =>
      isSuperAdmin || (isAuthenticated && permissions.includes(permission)),
    [isAuthenticated, isSuperAdmin, permissions],
  );

  return {
    isAdmin: isAuthenticated && hasAdminRole(roles),
    isSuperAdmin,
    permissions,
    hasPermission,
  };
}
